import {
  Label,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions
} from '@headlessui/react'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/24/solid'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'

import { ASSESSMENT_LANGUAGE_FEATURES } from '../helpers/mail-template-language'
import { LockPill, useLockTexts } from './pricing/lock-badge'

// Geteilter Default statt eines neuen Arrays bei jedem Render
const NO_LOCKED_OPTIONS = Object.freeze([])

// Pille einer gesperrten Option mit Paketname wie bei LockBadge. Ohne
// Feature bleibt sie neutral, dann gibt es keinen Paketnamen.
const FeatureLockPill = ({ feature }) => {
  const { label, includedText, ariaLabel } = useLockTexts(feature)

  return (
    <span title={includedText} className='ml-2 inline-flex'>
      <LockPill label={label} />
      <span className='sr-only'>{ariaLabel}</span>
    </span>
  )
}

FeatureLockPill.propTypes = { feature: PropTypes.string.isRequired }

const NeutralLockPill = () => {
  const { t } = useTranslation()

  return (
    <span className='ml-2 inline-flex'>
      <LockPill label={t('pricing.lock.locked')} />
    </span>
  )
}

const SelectMenu = ({
  id,
  label,
  description,
  options,
  className,
  onChange,
  value,
  defaultValue,
  disabled,
  translate,
  position,
  lockedOptions,
  onLockedSelect,
  lockedFeatures
}) => {
  const { t } = useTranslation()

  const [uncontrolledKey, setUncontrolledKey] = useState(defaultValue)

  // Kontrolliert, sobald ein value uebergeben wird. Vorher hielt die
  // Komponente ihren Wert ausschliesslich im eigenen useState und synchte ihn
  // nie mit der Prop: nach einem Refetch konnte die Anzeige von dem Wert
  // abweichen, der tatsaechlich gespeichert war.
  const isControlled = value !== undefined
  const requestedKey = isControlled ? value : uncontrolledKey

  // Liegt der Key nicht in options, faellt die Anzeige auf die erste Option
  // zurueck, so wie ein natives <select> es tut. Das passierte real, als die
  // Settings-Seite den Wert aus dem URL-Hash speiste und ein veraltetes
  // Bookmark einen Key lieferte, den tabsConfig nicht mehr kannte.
  //
  // Der Fallback muss VOR dem Listbox greifen, nicht nur im Button. Sonst
  // zeigt der Button die erste Option, waehrend das Dropdown keine Option als
  // ausgewaehlt markiert, weil Listbox weiter den ungueltigen Key vergleicht.
  //
  // Bewusst ohne onChange: den Wert des Aufrufers waehrend des Renderns still
  // umzuschreiben waere ein Seiteneffekt an der falschen Stelle. Korrigiert
  // wird nur die Darstellung.
  const selectedOption =
    options.find(([key]) => key === requestedKey) || options[0]
  const selectedKey = selectedOption?.[0]

  const selectedValue = useMemo(() => {
    // options darf leer sein (Default-Prop), dann gibt es nichts anzuzeigen.
    const label = selectedOption?.[1] || ''

    return {
      value: translate ? t(label) : label,
      subvalue: selectedOption?.[2]
    }
  }, [selectedOption, translate, t])

  // Gesperrte Optionen bleiben sichtbar und wählbar, ändern den Wert aber
  // nicht. Die Auswahl geht an onLockedSelect, der Aufrufer öffnet dann den
  // Upgrade-Dialog (Seite) oder zeigt UpgradeInline (Modal). Die gewählte
  // Option gilt nie als gesperrt, wie in SelectMenuWithDescription.
  const isLocked = (key) => key !== selectedKey && lockedOptions.includes(key)

  const handleChange = (nextValue) => {
    if (isLocked(nextValue)) {
      onLockedSelect?.(nextValue)
      return
    }

    if (!isControlled) setUncontrolledKey(nextValue)
    onChange(nextValue)
  }

  return (
    <div className={className}>
      <Listbox
        disabled={disabled}
        value={selectedKey}
        onChange={(value) => handleChange(value)}
      >
        {label && (
          <Label className='block text-sm font-medium text-gray-700'>
            {label}
          </Label>
        )}
        <div className={clsx('relative', label && 'mt-2')}>
          <ListboxButton
            className={clsx(
              'relative w-full border-gray-300 bg-white py-2 pl-3 pr-10 text-left text-sm',
              disabled
                ? 'border-l'
                : 'cursor-pointer rounded-md border shadow-sm'
            )}
          >
            <span className='flex items-center'>
              <ReactMarkdown>{selectedValue.value}</ReactMarkdown>
              {selectedValue.subvalue && (
                <span className='ml-1.5 text-sm text-gray-700'>
                  {selectedValue.subvalue}
                </span>
              )}
            </span>
            <span
              className={clsx(
                'pointer-events-none absolute inset-y-0 right-0 pr-2',
                disabled ? 'hidden' : 'flex items-center'
              )}
            >
              <ChevronUpDownIcon
                className='h-5 w-5 text-gray-400'
                aria-hidden='true'
              />
            </span>
          </ListboxButton>
          {/* anchor rendert die Liste per Portal an body und positioniert sie
              mit Floating UI. Fehlt unten der Platz, klappt sie nach oben, und
              sie wird nie höher als der verfügbare Platz. Vorher lag sie im
              Seitenbaum und verschwand z. B. hinter dem fixierten Footer von
              "Neue Stelle" (z-10, ebenfalls per Portal an body). Weil die Liste
              jetzt an body hängt, muss sie über allen Dialogen liegen
              (Modal z-40, Slideover bis z-50), sonst öffnet sie sich
              unsichtbar hinter dem Modal. Deshalb z-[60]. */}
          <ListboxOptions
            id={id}
            anchor={{
              to: position === 'top' ? 'top start' : 'bottom start',
              gap: 4,
              padding: 8
            }}
            className={clsx(
              'z-[60] w-[var(--button-width)] overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 [--anchor-max-height:15rem] focus:outline-none',
              'data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in'
            )}
          >
            {options.map((option, index) => (
              <ListboxOption
                key={index}
                data-locked={isLocked(option[0]) ? '' : undefined}
                className={({ active }) =>
                  clsx(
                    active ? 'bg-blue-600 text-white' : 'text-gray-900',
                    'relative cursor-pointer select-none py-2 pl-3 pr-9'
                  )
                }
                value={option[0]}
              >
                {({ selected, active }) => {
                  let value = translate ? t(option[1]) : option[1]
                  if (selected) value = value.replaceAll('**', '')

                  return (
                    <>
                      <span
                        className={clsx(
                          selected ? 'font-bold' : 'font-normal',
                          'flex items-center'
                        )}
                      >
                        <ReactMarkdown>{value}</ReactMarkdown>
                        {option[2] && (
                          <span
                            className={clsx(
                              'ml-1.5 text-sm',
                              active ? 'text-white' : 'text-gray-700'
                            )}
                          >
                            {option[2]}
                          </span>
                        )}
                        {isLocked(option[0]) &&
                          (lockedFeatures?.[option[0]] ? (
                            <FeatureLockPill
                              feature={lockedFeatures[option[0]]}
                            />
                          ) : (
                            <NeutralLockPill />
                          ))}
                      </span>

                      {selected && (
                        <span
                          className={clsx(
                            active ? 'text-white' : 'text-blue-600',
                            'absolute inset-y-0 right-0 flex items-center pr-4'
                          )}
                        >
                          <CheckIcon className='h-5 w-5' aria-hidden='true' />
                        </span>
                      )}
                    </>
                  )
                }}
              </ListboxOption>
            ))}
          </ListboxOptions>
        </div>
        {description && (
          <p className='mt-2 text-xs italic text-gray-500'>{description}</p>
        )}
      </Listbox>
    </div>
  )
}

SelectMenu.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  description: PropTypes.string,
  options: PropTypes.array, // [ key, value ]
  className: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  disabled: PropTypes.bool,
  translate: PropTypes.bool,
  // Bevorzugte Richtung. Reicht der Platz nicht, klappt die Liste von selbst
  // um, 'auto' verhält sich deshalb wie 'bottom'.
  position: PropTypes.oneOf(['bottom', 'top', 'auto']),
  // Keys gesperrter Optionen, etwa aus getLockedAssessmentLanguages. Sie
  // zeigen eine Schloss-Pille, eine Auswahl ruft onLockedSelect(key) statt
  // onChange. Leer verhält sich das Menü wie bisher.
  lockedOptions: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  onLockedSelect: PropTypes.func,
  // Feature-Key (FEATURES in features/pricing/plans.js) je gesperrter
  // Option für den Paketnamen in der Pille. Standard sind die Sprachen, weil
  // heute nur die Sprachwahl Optionen sperrt. Ohne Eintrag bleibt die Pille
  // neutral.
  lockedFeatures: PropTypes.objectOf(PropTypes.string)
}

SelectMenu.defaultProps = {
  id: 'location',
  label: undefined,
  description: undefined,
  options: [],
  value: undefined,
  defaultValue: undefined,
  disabled: false,
  translate: true,
  position: 'bottom',
  lockedOptions: NO_LOCKED_OPTIONS,
  onLockedSelect: undefined,
  lockedFeatures: ASSESSMENT_LANGUAGE_FEATURES
}

export default SelectMenu
